import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Avatar,
    Select,
    MenuItem,
    FormControl,
    Menu,
    Paper
} from '@mui/material';
import { DownloadIcon } from 'icons/materialIcons';
import { useAuth } from 'contexts/AuthContext';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext';
import { useNavigate } from 'react-router-dom';
// Import the saveLead function directly from the API
import { saveLead } from 'apis/leadCaptureApi';
import { getCardsByUser } from 'apis/userApi';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { useTeam } from 'team/contexts/teamContext';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { SearchOutlinedIcon, KeyboardArrowDownOutlinedIcon } from 'icons/materialIcons';
import { InputBase } from '@mui/material';

// Import existing components from individual plan
import LeadValidationUtils from 'components/contacts/contactMain/contactsMenu/addLeads/LeadValidationUtils';
import FileImportUtils from 'utils/contacts/FileImportUtils';
import CSVReview from 'components/contacts/contactMain/contactsMenu/addLeads/CSVReview';
import FileUploader from 'components/contacts/contactMain/contactsMenu/addLeads/FileUploader';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

// Import team-specific components
import { TEAM_MEMBER_FIELDS } from 'team/shared/features/addMember/TeamMemberFields';
import TeamMemberRequiredFields from 'team/shared/features/addMember/TeamMemberRequiredFields';

const TeamImportViaCSV = () => {
    const navigate = useNavigate();
    const [csvData, setCsvData] = useState(null);
    const [validatedLeads, setValidatedLeads] = useState(null); // Store validated leads from CSVReview
    const [columns, setColumns] = useState([]);
    const [step, setStep] = useState(1);
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importStatus, setImportStatus] = useState({
        total: 0,
        success: 0,
        failed: 0
    });
    const [importError, setImportError] = useState('');
    const [teamMemberCards, setTeamMemberCards] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState('');
    const [loadingCards, setLoadingCards] = useState(true);
    const [cardSearchQuery, setCardSearchQuery] = useState('');
    const [showCardList, setShowCardList] = useState(false); // Start with dropdown closed
    const cardListRef = useRef(null);
    const cardDropdownAnchorEl = useRef(null);
    const csvReviewRef = useRef(null);

    // Import context hooks
    const { authState } = useAuth();
    const { addContact, fetchContacts } = useLeadCaptureContext();
    const { currentTeamId } = useTeam();

    // Notification state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Auto-map CSV columns to lead fields
    const getInitialMappings = () => {
        if (!columns || columns.length === 0) return {};

        const mappings = {};

        // Try to auto-map common column names
        columns.forEach(col => {
            const normalizedCol = col.trim().toLowerCase();

            // Map common variations to lead fields
            if (normalizedCol.includes('name') || normalizedCol.includes('full name')) {
                mappings['leadName'] = col;
            } else if (normalizedCol.includes('email') || normalizedCol === 'email') {
                mappings['leadEmail'] = col;
            } else if (normalizedCol.includes('phone') || normalizedCol.includes('mobile')) {
                mappings['leadPhoneNumber'] = col;
            } else if (normalizedCol.includes('job') || normalizedCol.includes('title') || normalizedCol.includes('position')) {
                mappings['leadJobTitle'] = col;
            } else if (normalizedCol.includes('company') || normalizedCol.includes('organization')) {
                mappings['leadCompany'] = col;
            } else if (normalizedCol.includes('address') || normalizedCol.includes('location')) {
                mappings['leadAddress'] = col;
            }
        });

        return mappings;
    };

    // Fetch all team member cards on component mount
    useEffect(() => {
        const fetchTeamMemberCards = async () => {
            if (!currentTeamId) return;

            try {
                setLoadingCards(true);
                // Fetch all active team members with their default cards
                const response = await getTeamMembers(currentTeamId, { status: 'active' });
                const members = response?.members || [];

                // Extract all default cards from team members
                const allCards = [];
                members.forEach(member => {
                    if (member.defaultCard && member.defaultCard._id) {
                        // Add userId to card object from member for matching
                        const cardWithUserId = {
                            ...member.defaultCard,
                            userId: member.userId || member.user?.userId
                        };
                        allCards.push(cardWithUserId);
                    }
                });

                setTeamMemberCards(allCards);

                // Find and set the default card as selected (prefer logged-in user's card)
                const loggedInUserCard = allCards.find(card =>
                    card.userId === authState?.user?.userId
                );
                if (loggedInUserCard) {
                    setSelectedCardId(loggedInUserCard._id);
                } else if (allCards.length > 0) {
                    // Fallback to first card if logged-in user's card not found
                    setSelectedCardId(allCards[0]._id);
                }
            } catch (error) {
                console.error('Failed to fetch team member cards:', error);
                setImportError('Failed to load team member cards. Please try again.');
            } finally {
                setLoadingCards(false);
            }
        };

        if (currentTeamId) {
            fetchTeamMemberCards();
        }
    }, [currentTeamId, authState?.user?.userId]);

    // Scroll to selected card when dropdown opens
    useEffect(() => {
        if (showCardList && selectedCardId && cardListRef.current) {
            const selectedElement = cardListRef.current.querySelector(`[data-card-id="${selectedCardId}"]`);
            if (selectedElement) {
                setTimeout(() => {
                    selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }, 100);
            }
        }
    }, [showCardList, selectedCardId]);

    // File selection handler
    const handleFileSelect = useCallback(async (selectedFile) => {
        try {
            // Import file with improved parsing
            const { data, columns } = await FileImportUtils.importFile(selectedFile);

            // Log for debugging
            console.log("Imported file:", selectedFile.name);
            console.log("Imported columns:", columns);
            console.log("Data rows:", data.length);

            // Filter out empty rows to ensure clean data
            const validRows = data.filter(row => {
                return Object.values(row).some(val =>
                    val !== null && val !== undefined && val !== '');
            });

            setCsvData(validRows);
            setColumns(columns);
            setStep(2);
        } catch (error) {
            console.error("File import error:", error);
            setImportError(error.message);
        }
    }, []);

    // Function to actually import the leads
    const importLeads = async (leads) => {
        if (!leads || !leads.length) {
            console.error("No leads to import");
            return;
        }

        // Check if user ID is available
        if (!authState || !authState.user || !authState.user.userId) {
            console.error("Cannot import leads: userId is not available");
            setImportError("User ID is not available. Please ensure you are logged in and try again.");
            return;
        }

        console.log("User ID from auth state:", authState.user.userId);
        console.log("Selected Card ID:", selectedCardId);

        setIsImporting(true);
        setImportStatus({
            total: leads.length,
            success: 0,
            failed: 0
        });
        setStep(4);

        const results = {
            success: 0,
            failed: 0,
            errors: []
        };

        for (let i = 0; i < leads.length; i++) {
            const lead = leads[i];
            setImportProgress(Math.round(((i + 1) / leads.length) * 100));

            try {
                // Explicitly prepare lead data with user ID and selected card ID
                const leadData = {
                    ...lead,
                    leadSource: 'csv-import',
                    userId: authState.user.userId,
                    // Only include cardId if a card is selected
                    ...(selectedCardId ? { cardId: selectedCardId } : {})
                };

                console.log("Sending team lead data to API:", leadData);

                // Use the imported saveLead function directly
                const response = await saveLead(leadData);

                if (response && response.success) {
                    if (response.lead && response.lead._id) {
                        // Ensure the lead has all required fields for UI display
                        const contactToAdd = {
                            ...response.lead,
                            // Ensure these fields exist for UI compatibility
                            leadName: response.lead.leadName || response.lead.fullName || '',
                            leadEmail: response.lead.leadEmail || response.lead.email || '',
                            leadPhoneNumber: response.lead.leadPhoneNumber || response.lead.phone || '',
                            leadJobTitle: response.lead.leadJobTitle || response.lead.jobTitle || '',
                            leadCompany: response.lead.leadCompany || response.lead.company || '',
                            leadSource: response.lead.leadSource || 'csv-import',
                            createdAt: response.lead.createdAt || new Date().toISOString()
                        };

                        addContact(contactToAdd);
                        results.success++;
                    } else {
                        results.failed++;
                        results.errors.push({
                            email: leadData.leadEmail,
                            error: 'Lead appears to be saved but no valid lead object returned'
                        });
                    }
                } else {
                    results.failed++;
                    results.errors.push({
                        email: leadData.leadEmail,
                        error: response?.message || 'Unknown error'
                    });
                }
            } catch (error) {
                console.error("Error saving team lead:", error);
                results.failed++;
                results.errors.push({
                    email: lead.leadEmail || 'Unknown email',
                    error: error.message || 'Unknown error'
                });
            }

            setImportStatus({
                total: leads.length,
                success: results.success,
                failed: results.failed
            });
        }

        setTimeout(async () => {
            setIsImporting(false);

            if (results.failed > 0) {
                const errorDetails = results.errors
                    .slice(0, 5)
                    .map(e => `• ${e.email}: ${e.error}`)
                    .join('\n');

                const additionalErrors = results.errors.length > 5
                    ? `\n• ...and ${results.errors.length - 5} more errors`
                    : '';

                setSnackbar({
                    open: true,
                    message: `Import completed with ${results.success} successful and ${results.failed} failed team lead imports. Check console for details.`,
                    severity: 'warning'
                });

                // Log detailed errors to console
                console.error('Team lead import errors:', results.errors);
            } else {
                setSnackbar({
                    open: true,
                    message: `Successfully imported ${results.success} team leads!`,
                    severity: 'success'
                });
            }

            // Refresh the contacts list to ensure UI is updated
            if (authState?.user?.userId) {
                await fetchContacts(authState.user.userId);
            }

            // Navigate to team leads page after successful import
            setTimeout(() => {
                navigate('/team/leads');
            }, 2000); // Give time for snackbar to show

            // Import completed successfully
        }, 1000);
    };

    // Generate team member template for CSV import
    const generateTeamMemberImportTemplate = () => {
        return [
            {
                'Email Of Lead': 'john.doe@example.com',
                'Name': 'John Doe',
                'Number': '+1234567890',
                'Job Title': 'Senior Manager',
                'Company': 'Acme Corporation',
                'Website': 'www.johndoe.com',
                'Location': 'New York, NY'
            }
        ];
    };

    // Generate and download a template CSV
    const downloadTemplate = () => {
        const template = generateTeamMemberImportTemplate();
        FileImportUtils.downloadTemplate(template, 'team_leads_import_template.csv');
    };

    // Render different content based on current step
    const renderContent = () => {
        if (step === 1) {
            return (
                <>
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1 }}>How to format the CSV</Typography>
                                <Typography variant="body1" sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 400,
                                    color: '#828282',
                                    lineHeight: 1.5,
                                    mb: 2
                                }}>
                                    Take a look at the possible columns below or download the CSV template.
                                </Typography>
                            </Box>
                            <Button
                                startIcon={<DownloadIcon />}
                                onClick={downloadTemplate}
                                sx={{
                                    color: '#29AEF8',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Download CSV Template
                            </Button>
                        </Box>
                        <TeamMemberRequiredFields />
                    </Box>
                    <FileUploader onFileSelect={handleFileSelect} />
                    {importError && (
                        <Alert
                            severity="error"
                            sx={{ mt: 3 }}
                        >
                            {importError}
                        </Alert>
                    )}
                </>
            );
        }

        if (step === 2 && csvData && columns.length > 0) {
            return (
                <CSVReview
                    ref={csvReviewRef}
                    csvData={csvData}
                    columns={columns}
                    initialMappings={getInitialMappings()}
                    onComplete={(leads) => {
                        // Store validated leads and move to assignment step
                        setValidatedLeads(leads);
                        setStep(3);
                        // Close dropdown when entering step 3
                        setShowCardList(false);
                    }}
                    onCancel={() => setStep(1)}
                    userCards={teamMemberCards}
                    selectedCardId={selectedCardId}
                    setSelectedCardId={setSelectedCardId}
                    loadingCards={loadingCards}
                />
            );
        }

        if (step === 3) {
            // Filter cards based on search query
            // Always include the selected card even if it doesn't match the search
            const filteredCards = teamMemberCards.filter(card => {
                // Always show selected card
                if (selectedCardId && card._id === selectedCardId) return true;

                // If no search query, show all cards
                if (!cardSearchQuery) return true;

                // Otherwise filter by search query
                const query = cardSearchQuery.toLowerCase();
                const fullName = (card.contact_info?.fullName || card.cardName || '').toLowerCase();
                const email = (card.contact_info?.email || '').toLowerCase();
                const jobTitle = (card.contact_info?.jobTitle || '').toLowerCase();
                const company = (card.contact_info?.company || '').toLowerCase();
                return fullName.includes(query) || email.includes(query) ||
                    jobTitle.includes(query) || company.includes(query);
            });

            return (
                <Box>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 3 }}>
                        {/* Left side - Heading and description */}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                Lead Assignment
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#828282', fontSize: '0.9rem' }}>
                                Assign newly imported leads to a member of your team
                            </Typography>
                        </Box>

                        {/* Right side - Card selection with search (matching subteam dropdown style) */}
                        <Box sx={{ flex: '0 0 350px', maxWidth: '350px', marginTop: '8px' }}>
                            {/* Selected Card Button - matching edit member navbar style */}
                            {selectedCardId && (() => {
                                const selectedCard = teamMemberCards.find(c => c._id === selectedCardId);
                                return selectedCard ? (
                                    <Button
                                        ref={cardDropdownAnchorEl}
                                        onClick={() => {
                                            setShowCardList(true);
                                            setCardSearchQuery('');
                                        }}
                                        variant="contained"
                                        sx={{
                                            backgroundColor: '#fff',
                                            color: '#e0e0e0',
                                            fontWeight: '500',
                                            borderRadius: '8px',
                                            border: '1px #e0e0e0 solid',
                                            textTransform: 'none',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                            padding: '6.5px 16px',
                                            boxShadow: 'none',
                                            marginBottom: '12px',
                                            '&:hover': {
                                                backgroundColor: '#fff',
                                                color: '#e0e0e0',
                                                boxShadow: 'none',
                                            },
                                            '& .MuiButton-startIcon': {
                                                marginRight: 1,
                                            },
                                            '& .MuiButton-endIcon': {
                                                marginLeft: 1,
                                            },
                                        }}
                                        startIcon={
                                            <Avatar
                                                src={selectedCard.profilePictureUrl || avatarPlaceholder}
                                                alt={selectedCard.contact_info?.fullName || selectedCard.cardName}
                                                sx={{
                                                    width: 25,
                                                    height: 25,
                                                    border: '2px solid #ffffff',
                                                }}
                                            />
                                        }
                                        endIcon={<KeyboardArrowDownOutlinedIcon />}
                                    >
                                        <Typography sx={{ color: '#000', flexGrow: 1, textAlign: 'left', fontSize: "0.9rem", marginLeft: "10px" }}>
                                            {selectedCard.contact_info?.fullName || selectedCard.cardName || 'Select Card'}
                                        </Typography>
                                    </Button>
                                ) : null;
                            })()}

                            {/* Card Dropdown Menu - matching subteam dropdown style */}
                            {cardDropdownAnchorEl.current && (
                                <Menu
                                    anchorEl={cardDropdownAnchorEl.current}
                                    open={showCardList}
                                    onClose={() => setShowCardList(false)}
                                    disableAutoFocusItem
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left'
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left'
                                    }}
                                    PaperProps={{
                                        sx: {
                                            borderRadius: '12px',
                                            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                                            width: {
                                                xs: '70vw',
                                                sm: '320px',
                                                md: '350px'
                                            },
                                            maxWidth: '350px',
                                            maxHeight: '80vh',
                                            overflow: 'hidden',
                                            marginTop: '12px',
                                            padding: '12px',
                                            backgroundColor: '#fafafa'
                                        }
                                    }}
                                    MenuListProps={{
                                        sx: {
                                            padding: 0
                                        }
                                    }}
                                    slotProps={{
                                        backdrop: {
                                            sx: {
                                                cursor: 'default'
                                            }
                                        }
                                    }}
                                >
                                    {/* Search Bar */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            backgroundColor: 'white',
                                            borderRadius: '20px',
                                            border: '1px solid #e3e3e3',
                                            padding: '8px 16px',
                                            marginBottom: '12px',
                                            '&:hover': {
                                                boxShadow: 2,
                                            },
                                        }}
                                    >
                                        <InputBase
                                            fullWidth
                                            value={cardSearchQuery}
                                            onChange={(e) => setCardSearchQuery(e.target.value)}
                                            placeholder="Search..."
                                            sx={{
                                                fontSize: '0.7rem',
                                                color: '#4f4f4f',
                                                '& .MuiInputBase-input::placeholder': {
                                                    color: '#828282',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    </Box>

                                    {/* Cards list */}
                                    <Box
                                        ref={cardListRef}
                                        sx={{
                                            maxHeight: '60vh',
                                            overflowY: 'auto'
                                        }}
                                    >
                                        {loadingCards ? (
                                            <Box sx={{ padding: 2, textAlign: 'center' }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : filteredCards.length === 0 ? (
                                            <Box sx={{ padding: 2, textAlign: 'center' }}>
                                                <Typography sx={{ fontSize: '0.875rem', color: '#828282' }}>
                                                    {cardSearchQuery ? 'No team members found' : 'No team members available'}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            filteredCards.map((card) => {
                                                const isSelected = selectedCardId === card._id;
                                                return (
                                                    <MenuItem
                                                        key={card._id}
                                                        data-card-id={card._id}
                                                        onClick={() => {
                                                            setSelectedCardId(card._id);
                                                            setCardSearchQuery('');
                                                            setShowCardList(false);
                                                        }}
                                                        selected={isSelected}
                                                        sx={{
                                                            borderRadius: '8px',
                                                            marginBottom: '4px',
                                                            '&.Mui-selected': {
                                                                backgroundColor: '#f0f0f0',
                                                                '&:hover': {
                                                                    backgroundColor: '#e0e0e0'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                                            <Avatar
                                                                src={card.profilePictureUrl || avatarPlaceholder}
                                                                alt={card.contact_info?.fullName || card.cardName}
                                                                sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>
                                                                    {card.contact_info?.fullName || card.cardName || 'Unnamed Card'}
                                                                </Typography>
                                                                {(card.contact_info?.jobTitle || card.contact_info?.company) && (
                                                                    <Typography sx={{ fontSize: '0.8rem', color: '#828282', marginTop: '2px' }}>
                                                                        {[card.contact_info?.jobTitle, card.contact_info?.company].filter(Boolean).join(' • ')}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </MenuItem>
                                                );
                                            })
                                        )}
                                    </Box>
                                </Menu>
                            )}

                        </Box>
                    </Box>
                </Box>
            );
        }

        if (step === 4) {
            return (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '400px'
                }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 500 }}>
                        Importing Team Leads
                    </Typography>

                    <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
                        <CircularProgress
                            variant="determinate"
                            value={importProgress}
                            size={120}
                            thickness={5}
                            sx={{ color: '#29AEF8' }}
                        />
                        <Box
                            sx={{
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography variant="h4" component="div" color="text.secondary">
                                {`${importProgress}%`}
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Processing {importStatus.success} of {importStatus.total} team leads
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="success.main">
                                {importStatus.success}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Successful
                            </Typography>
                        </Box>

                        {importStatus.failed > 0 && (
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" color="error">
                                    {importStatus.failed}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Failed
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            );
        }

        return null;
    };

    return (
        <>
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '24px',
                boxSizing: 'border-box'
            }}>
                <Box sx={{
                    width: '100%',
                    maxWidth: '1200px',
                    height: 'auto',
                    display: 'flex',
                    padding: '42px',
                    borderRadius: '18px',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box'
                }}>
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 4
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {step === 1 && (
                                    <Typography variant="h5">
                                        Import Leads
                                    </Typography>
                                )}
                                {step === 2 && (
                                    <Typography variant="h5">
                                        Field Mapping
                                    </Typography>
                                )}
                                {step === 3 && (
                                    <Typography variant="h5">
                                        Configuration & Settings
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                {/* Step 1 - Upload CSV - Always available */}
                                <Box
                                    onClick={() => setStep(1)}
                                    sx={{
                                        backgroundColor: step === 1 ? '#FFFFFF' : '#F7F7F7',
                                        color: '#4F4F4F',
                                        border: step === 1 ? '1px solid #E0E0E0' : 'none',
                                        padding: '0 24px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: '100px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: step === 1 ? '#FFFFFF' : '#EEEEEE'
                                        }
                                    }}
                                >
                                    1 - Upload CSV
                                </Box>
                                {/* Step 2 - Mapping - Available when csvData exists */}
                                <Box
                                    onClick={csvData ? () => setStep(2) : undefined}
                                    sx={{
                                        backgroundColor: step === 2 ? '#FFFFFF' : '#F7F7F7',
                                        color: csvData ? '#4F4F4F' : '#BDBDBD',
                                        border: step === 2 ? '1px solid #E0E0E0' : 'none',
                                        padding: '0 24px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: '100px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        cursor: csvData ? 'pointer' : 'not-allowed',
                                        opacity: csvData ? 1 : 0.6,
                                        '&:hover': csvData ? {
                                            backgroundColor: step === 2 ? '#FFFFFF' : '#EEEEEE'
                                        } : {}
                                    }}
                                >
                                    2 - Mapping
                                </Box>
                                {/* Step 3 - Settings - Available when csvData exists (upload complete) or validatedLeads exists (mapping complete) */}
                                <Box
                                    onClick={(csvData || validatedLeads || step >= 2) ? () => setStep(3) : undefined}
                                    sx={{
                                        backgroundColor: step === 3 ? '#FFFFFF' : '#F7F7F7',
                                        color: (csvData || validatedLeads || step >= 2) ? '#4F4F4F' : '#BDBDBD',
                                        border: step === 3 ? '1px solid #E0E0E0' : 'none',
                                        padding: '0 24px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: '100px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        cursor: (csvData || validatedLeads || step >= 2) ? 'pointer' : 'not-allowed',
                                        opacity: (csvData || validatedLeads || step >= 2) ? 1 : 0.6,
                                        '&:hover': (csvData || validatedLeads || step >= 2) ? {
                                            backgroundColor: step === 3 ? '#FFFFFF' : '#EEEEEE'
                                        } : {}
                                    }}
                                >
                                    3 - Settings
                                </Box>
                                {csvData && (
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            if (step === 2) {
                                                // Validate and complete mapping before moving to step 3
                                                if (csvReviewRef.current) {
                                                    csvReviewRef.current.validateAndComplete();
                                                }
                                            } else if (step === 3) {
                                                if (selectedCardId) {
                                                    importLeads(validatedLeads);
                                                } else {
                                                    setSnackbar({
                                                        open: true,
                                                        message: 'Please select a team member card',
                                                        severity: 'warning'
                                                    });
                                                }
                                            }
                                        }}
                                        disabled={step === 3 && (!selectedCardId || loadingCards)}
                                        sx={{
                                            backgroundColor: '#29AEF8',
                                            color: '#FFFFFF',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            padding: '0 24px',
                                            height: '40px',
                                            borderRadius: '100px',
                                            fontSize: '12px',
                                            '&:hover': {
                                                backgroundColor: '#2196F3'
                                            },
                                            '&:disabled': {
                                                backgroundColor: '#E0E0E0',
                                                color: '#9E9E9E'
                                            }
                                        }}
                                    >
                                        {step === 3 ? 'Import Leads' : 'Continue'}
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        {renderContent()}
                    </Box>
                </Box>
            </Box>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleSnackbarClose}
            />
        </>
    );
};

export default TeamImportViaCSV;